<script>
/**
 * 开始报价 / 查看报价 - 模态框内部组件
 *
 * 由 SimpleTable 通过 fields.js 的 modals.createOrView 配置渲染，接收：
 *   params    打开时 setParams 注入的参数（字段名统一为首字母小写的驼峰）：
 *             { category, id, row, rowIndex, shipmentNumber, hasBattery, ..., expenseItem, ... }
 *   close     关闭模态框的方法
 *   setTitle  设置模态框标题的方法
 *   refresh   重新拉取表格数据的方法
 *
 * 组件不做初始化（没有 mounted / watch）、也不设默认值：
 * 数据全部由 params 派生，参数由 DSL 在 setParams 之前请求接口组装好。
 */

// 单元格渲染：把 DSL 里的 render(h, value) 渲染成 vnode
// 未配 render 时直接输出文本；空值统一显示 '-'
const CellRender = {
  functional: true,
  props: {
    render: {
      type: Function,
      default: null
    },
    value: {
      default: null
    }
  },
  render(h, context) {
    const { render, value } = context.props;
    if (value === "" || value === null || value === undefined) return h("span", "-");
    return typeof render === "function" ? render(h, value) : h("span", String(value));
  }
};

const renderValue = (h, value) => {
  const flag = value !== '否' && value !== '无'
  return h("Tag", { props: { size: "small", color: flag ? "warning" : "success" } }, flag ? value : "否");
};

// 只读字段展示配置：label 文案 + params 里的取值 key（小驼峰）
// 可选 render(h, value)：自定义展示（与表格列的 render 写法一致，如布尔 -> 是/否）
const waybillFields = [
  { label: "运单号", key: "shipmentNumber" },
  { label: "客户账号", key: "customerAccount" },
  // 以下三个布尔字段用 Tag 展示：size 统一 small，value 为否 -> success，否则 -> warning
  { label: "是否带电", key: "hasBattery", render: (h, value) => renderValue(h, value) },
  { label: "是否单独报关", key: "separateDeclaration", render: (h, value) => renderValue(h, value) },
  { label: "COD金额", key: "codAmount" },
  { label: "是否购买保险", key: "hasInsurance", render: (h, value) => renderValue(h, value) },
  { label: "服务项目", key: "serviceItem" },
  { label: "起运国家", key: "fromCountry" },
  { label: "目的国家", key: "toCountry" },
  { label: "派送城市", key: "city" },
  { label: "服务仓库", key: "serviceWarehouse" },
  { label: "送仓仓库", key: "deliveryWarehouse" },
  { label: "运输方式", key: "transportMethod" },
  { label: "海外仓入仓时间", key: "inboundTime" },
];

const inboundFields = [
  { label: "收货仓库", key: "warehouseName" },
  { label: "收货仓入库状态", key: "inboundStatus", render: (h, value) => renderValue(h, value) },
  { label: "总箱数", key: "totalBox" },
  { label: "已入库箱数", key: "hasBox"},
  { label: "重量", key: "weight" },
  { label: "体积", key: "volume" },

  { label: "品名", key: "productName" },
  { label: "运输方式", key: "inboundTransportMethod" },
];

export default {
  name: "AssignChargeFee",
  components: { CellRender },
  props: {
    params: {
      type: Object,
      default: () => ({})
    },
    close: {
      type: Function,
      default: () => {}
    },
    setTitle: {
      type: Function,
      default: () => {}
    },
    refresh: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      // 仅有的两个可编辑字段的本地状态：undefined 表示「用户还没改过」，此时展示 params 里的值
      quoteForm: {
        unitPrice: undefined,
        remark: undefined
      },
      saving: false
    };
  },
  computed: {
    // 弹窗数据全部来自 params（接口返回后由 DSL 的 setParams 注入），组件不设默认值；
    // || {} 只是为了兜底 null —— 请求返回前 modal.params 是 null
    info() {
      return this.params || {};
    },
    // 单价：用户改过就用本地值，没改过就用 params 里的值（null 视为用户清空）
    unitPrice: {
      get() {
        return this.quoteForm.unitPrice === undefined ? this.info.unitPrice : this.quoteForm.unitPrice;
      },
      set(value) {
        this.quoteForm.unitPrice = value;
      }
    },
    // 备注：同上
    remark: {
      get() {
        return this.quoteForm.remark === undefined ? this.info.remark : this.quoteForm.remark;
      },
      set(value) {
        this.quoteForm.remark = value;
      }
    },
    // 总价 = 计费数量 × 单价，四舍五入保留两位小数（避免浮点相乘出现超长小数位）
    totalPrice() {
      const quantity = Number(this.info.chargeQuantity) || 0;
      const price = Number(this.unitPrice) || 0;
      return Math.round(quantity * price * 100) / 100;
    },
    // 表格行数据：每行 2 组「字段名 + 字段值」
    waybillRows() {
      return this.toTableRows(waybillFields);
    },
    inboundRows() {
      return this.toTableRows(inboundFields);
    }
  },
  methods: {
    // 把一维字段数组按每行 size 组切分，末尾不足的补 null，
    // 好处是模板里不用写额外判断，表格边框能对齐（空位渲染成空格子）
    toTableRows(fields, size = 2) {
      const rows = [];
      for (let i = 0; i < fields.length; i += size) {
        const row = fields.slice(i, i + size);
        while (row.length < size) row.push(null);
        rows.push(row);
      }
      return rows;
    },
    handleSubmit() {
      // TODO: 接入保存报价接口（提交后再 refresh + close）
      //   const payload = {
      //     id: this.info.id,
      //     unitPrice: this.unitPrice,
      //     remark: this.remark,
      //     totalPrice: this.totalPrice
      //   };
      this.$Message.success("保存成功");
      this.refresh();
      this.close();
    },
    handleCancel() {
      this.close();
    }
  }
};
</script>

<template>
<div class="assign-charge-fee">
  <div class="cards">
    <!-- 运单信息（只读表格） -->
    <Card class="info-card" :bordered="false" dis-hover>
      <div slot="title" class="card-title">运单信息</div>
      <table class="info-table">
        <colgroup>
          <col style="width: 18%">
          <col style="width: 32%">
          <col style="width: 18%">
          <col style="width: 32%">
        </colgroup>
        <tbody>
          <tr v-for="(row, rowIndex) in waybillRows" :key="rowIndex">
            <template v-for="(item, cellIndex) in row">
              <th class="label" :key="'l' + cellIndex">{{ item ? item.label : "" }}</th>
              <td :key="'v' + cellIndex">
                <CellRender v-if="item" :render="item.render" :value="info[item.key]" />
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </Card>

    <!-- 入仓信息（只读表格） -->
    <Card class="info-card" :bordered="false" dis-hover>
      <div slot="title" class="card-title">服务信息</div>
      <table class="info-table">
        <colgroup>
          <col style="width: 18%">
          <col style="width: 32%">
          <col style="width: 18%">
          <col style="width: 32%">
        </colgroup>
        <tbody>
          <tr v-for="(row, rowIndex) in inboundRows" :key="rowIndex">
            <template v-for="(item, cellIndex) in row">
              <th class="label" :key="'l' + cellIndex">{{ item ? item.label : "" }}</th>
              <td :key="'v' + cellIndex">
                <CellRender v-if="item" :render="item.render" :value="info[item.key]" />
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </Card>

    <!-- 报价信息：一行 3 个 -->
    <Card class="info-card" :bordered="false" dis-hover>
      <div slot="title" class="card-title">报价信息</div>
      <!-- 成本说明：只读，用 Alert 在表单上方整条提示 -->
      <Alert class="cost-alert" type="info" show-icon>
        <span>成本说明：{{ info.costDesc || "暂无" }}</span>
      </Alert>
      <Form class="quote-form" :label-width="80">
        <Row :gutter="12">
          <Col :span="8">
            <FormItem label="收费科目">
              <Input :value="info.expenseItem" disabled />
            </FormItem>
          </Col>
          <Col :span="8">
            <FormItem label="计费数量">
              <Input :value="info.chargeQuantity" disabled />
            </FormItem>
          </Col>
          <Col :span="8">
            <FormItem label="币种">
              <Input :value="info.chargeCurrency" disabled />
            </FormItem>
          </Col>
        </Row>
        <Row :gutter="12">
          <Col :span="8">
            <FormItem label="单价">
              <!-- active-change=false：输入过程中不逐键格式化/夹取，失焦时才按两位小数与最小值处理 -->
              <InputNumber
                v-model="unitPrice"
                :min="0.01"
                :step="0.01"
                :precision="2"
                :active-change="false"
                style="width: 100%"
              />
            </FormItem>
          </Col>
          <Col :span="8">
            <FormItem label="总价">
              <Input :value="totalPrice" disabled />
            </FormItem>
          </Col>
          <Col :span="8">
            <FormItem label="备注">
              <Input v-model="remark" type="textarea" :rows="2" placeholder="请输入备注" />
            </FormItem>
          </Col>
        </Row>
      </Form>
    </Card>
  </div>

  <div class="modal-actions">
    <Button @click="handleCancel">取消</Button>
    <Button type="primary" :loading="saving" style="margin-left: 8px" @click="handleSubmit">确定</Button>
  </div>
</div>
</template>

<style scoped>
.assign-charge-fee {
  display: flex;
  flex-direction: column;
}

/* 与 fields.js 里 createOrView 的 height(560) 配套：约 560 - 弹窗头(51) - body 内边距(32) - 底部按钮区(57)
   改弹窗高度时同步调整这里，内容超出时在卡片区内滚动 */
.cards {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.info-card {
  margin-bottom: 12px;
}

.info-card:last-child {
  margin-bottom: 0;
}

::v-deep .info-card .ivu-card-body {
  padding: 12px;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
}

/* 只读信息表格：字段名列灰底、全边框 */
.info-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.info-table th,
.info-table td {
  border: 1px solid #e8eaec;
  padding: 6px 8px;
  line-height: 20px;
  text-align: left;
  font-weight: normal;
  vertical-align: middle;
  word-break: break-all;
}

.info-table th.label {
  background: #f5f7f9;
  color: #515a6e;
}

.info-table td {
  background: #fff;
  color: #17233d;
}

.cost-alert {
  margin-bottom: 12px;
}

::v-deep .quote-form .ivu-form-item {
  margin-bottom: 12px;
}

.modal-actions {
  padding-top: 12px;
  margin-top: 12px;
  text-align: right;
  border-top: 1px solid #e8eaec;
}
</style>
